import { useEffect, useState } from 'react';
import axios from 'axios';
import { BellRing } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

const API = process.env.REACT_APP_BACKEND_URL;

// One switch per e-mail notification type (GET/PUT /api/notifications/preferences).
export const NotificationPreferences = () => {
  const { token, user } = useAuth();
  const { isDark } = useTheme();
  const [prefs, setPrefs] = useState([]);
  const headers = { Authorization: `Bearer ${token}` };
  const muted = isDark ? 'text-[#94A3B8]' : 'text-gray-600';

  useEffect(() => {
    axios.get(`${API}/api/notifications/preferences`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setPrefs(r.data.preferences || []))
      .catch(() => setPrefs([]));
  }, [token]);

  const toggle = async (type, enabled) => {
    try {
      const r = await axios.put(`${API}/api/notifications/preferences`, { preferences: { [type]: enabled } }, { headers });
      setPrefs(r.data.preferences);
      toast.success(enabled ? 'Notification activée' : 'Notification désactivée');
    } catch {
      toast.error('Préférence non enregistrée');
    }
  };

  return (
    <section
      id="notifications"
      data-testid="notification-preferences"
      className={`rounded-2xl border p-5 mb-6 ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}
    >
      <div className="w-11 h-11 rounded-xl bg-[#81d8d0]/15 flex items-center justify-center mb-5">
        <BellRing size={22} className="text-[#81d8d0]" />
      </div>
      <h2 className="text-xl font-semibold mb-2">Notifications par e-mail</h2>
      <p className={`text-sm leading-6 mb-4 ${muted}`}>
        Envoyées à {user?.email || 'ton adresse'}. Les mails de bienvenue et de mot de passe oublié sont
        toujours envoyés.
      </p>
      <ul className="space-y-3">
        {prefs.map((p) => (
          <li key={p.type} className="flex items-center justify-between gap-4 text-sm">
            <span className={p.available ? '' : muted}>
              {p.label}{p.available ? '' : ' — bientôt disponible'}
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={p.available && p.enabled}
              disabled={!p.available}
              onClick={() => toggle(p.type, !p.enabled)}
              data-testid={`notif-${p.type}`}
              className={`relative w-11 h-6 shrink-0 rounded-full transition-colors disabled:opacity-40 ${
                p.available && p.enabled ? 'bg-[#81d8d0]' : isDark ? 'bg-white/15' : 'bg-gray-300'
              }`}
            >
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${
                p.available && p.enabled ? 'left-[22px]' : 'left-0.5'
              }`} />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default NotificationPreferences;
